<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
  FhButton,
  FhLink,
  FhText,
  FhTitle,
  FhCard,
} from '@fareharbor-com/beacon-vue';
import {
  postAcceptInvitation,
  type AcceptInvitationPayload,
} from '@/common/api/postAcceptInvitation';
import { handleThrownError } from '@/common/errors/handleThrownError';
import BaseFlexbox from '@/components/ui/BaseFlexbox/BaseFlexbox.vue';
import { config } from '@/configs';
import { InventoryEnvironment } from '@/types/InventoryEnvironment';

const currentEnvironment = config.ENVIRONMENT;
const isStagingOrLocal = computed(() =>
  [InventoryEnvironment.STAGING, InventoryEnvironment.LOCAL].includes(
    currentEnvironment,
  ),
);

const router = useRouter();
const route = useRoute();

const email = ref<string | null>(null);
const token = ref<string | null>(null);
const isSubmitting = ref(false);
const successMessage = ref('');
const isUserActive = ref(false);

onMounted(() => {
  email.value = (route.query.email as string) || null;
  token.value = (route.query.token as string) || null;
});

async function acceptInvitation() {
  isSubmitting.value = true;
  successMessage.value = '';
  isUserActive.value = false;

  try {
    const payload: AcceptInvitationPayload = {
      email: email.value,
      token: token.value,
    };
    const { data, error } = await postAcceptInvitation(payload);

    if (error) {
      throw error;
    }

    if (data?.message === 'User is already activated.') {
      successMessage.value = data.message;
      isUserActive.value = true;
    } else {
      successMessage.value =
        'Thank you for accepting our invitation. You will receive an email to set your password. You can close this tab.';
    }
  } catch (error) {
    handleThrownError({
      error,
      toastMessage:
        'An error occurred. Please check your network connection and try again.',
      log: true,
    });
  } finally {
    isSubmitting.value = false;
  }
}

const topBlurClass = computed(() =>
  isStagingOrLocal.value ? 'top-10' : 'top-0',
);
const loginFormHeight = computed(() =>
  isStagingOrLocal.value ? 'calc(100vh - 165px)' : 'calc(100vh - 150px)',
);
</script>

<template>
  <BaseFlexbox :style="{ height: loginFormHeight }">
    <div
      :class="[
        'flex flex-col fixed inset-x-0 z-50 flex justify-center items-center w-full h-screen backdrop-blur-sm ',
        topBlurClass,
      ]"
    >
      <BaseFlexbox
        justifyContent="center"
        class="relative w-full max-w-md p-6 border-gray-300"
      >
        <img
          src="@/assets/images/logo.png"
          alt="Marketplace Logo"
          class="h-14"
        />
      </BaseFlexbox>
      <FhCard
        elevated
        id="confirm-password-reset-modal"
        data-test-id="confirm-password-reset-modal"
        size="lg"
        class="bg-white shadow-lg rounded-lg overflow-hidden w-full max-w-md"
      >
        <div v-if="successMessage" class="text-center">
          <FhText variant="body-lg" class="mt-4">{{ successMessage }}</FhText>

          <FhLink
            v-if="isUserActive"
            variant="primary"
            @click.prevent="router.push({ name: 'login' })"
            data-test-id="back-to-login-link"
            href="#"
            class="mt-2 inline-block"
          >
            Go to login
          </FhLink>
        </div>

        <template v-else>
          <FhTitle variant="display-xl">
            Welcome to FareHarbor Marketplace!
          </FhTitle>
          <FhText variant="body-md" class="mt-4" color="subtle">
            We are thrilled to introduce
            <strong>FareHarbor Marketplace</strong>
            , the next evolution of the FareHarbor Distribution Network.
          </FhText>
          <FhText variant="body-md" class="mt-4" color="subtle">
            We&apos;re transitioning from the Directory to Marketplace to offer
            a flexible, modern, and user-friendly experience.
          </FhText>
          <FhText variant="body-md" class="mt-4" color="subtle">
            Moving from the Directory to Marketplace is seamless, and we&apos;re
            here to support you every step of the way.
          </FhText>

          <div class="mt-4 text-center">
            <FhButton
              :disabled="isSubmitting"
              :loading="isSubmitting"
              type="button"
              fullWidth
              data-test-id="accept-invitation-button"
              class="mt-2"
              @click="acceptInvitation"
            >
              Accept invitation
            </FhButton>
          </div>
        </template>
      </FhCard>
    </div>
  </BaseFlexbox>
</template>
